import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Loader2, Eye } from "lucide-react";

const EMPTY = {
  title: "", summary: "", role: "", tools: "", tags: "", cover_image: "",
  featured: false, anonymized: false, order: 0,
  overview: "", problem: "", process: "", wireframes: "", final_ui: "", impact: "", learnings: "",
};

const TEXT_FIELDS = [
  ["overview", "Overview"], ["problem", "Problem"], ["process", "Process"],
  ["wireframes", "Wireframes"], ["final_ui", "Final UI"], ["impact", "Impact"], ["learnings", "Learnings"],
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  const load = () => {
    setLoading(true);
    api.listProjects().then(setProjects).catch(() => setProjects([])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, order: projects.length });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...p,
      tools: (p.tools || []).join(", "),
      tags: (p.tags || []).join(", "),
    });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      order: Number(form.order) || 0,
      tools: form.tools.split(",").map((s) => s.trim()).filter(Boolean),
      tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.updateProject(editing.id, payload);
        toast.success("Project updated");
      } else {
        await api.createProject(payload);
        toast.success("Project created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    try {
      await api.deleteProject(deleteId);
      toast.success("Project deleted");
      load();
    } catch (err) {
      toast.error("Failed to delete");
    } finally {
      setDeleteId(null);
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30">
      <header className="bg-white border-b border-black/5 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div>
            <span className="font-serif text-lg">Portfolio CMS</span>
            <span className="ml-3 text-xs text-foreground/50">{user?.email}</span>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" data-testid="view-site-btn" onClick={() => window.open("/", "_blank")}>
              <Eye size={15} className="mr-1.5" /> View site
            </Button>
            <Button variant="outline" size="sm" data-testid="logout-btn" onClick={() => { logout(); navigate("/admin/login"); }}>
              <LogOut size={15} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl text-foreground">Projects</h1>
            <p className="text-sm text-foreground/60 mt-1">{projects.length} case studies</p>
          </div>
          <Button data-testid="add-project-btn" onClick={openCreate} className="bg-primary hover:bg-[hsl(242,47%,28%)]">
            <Plus size={16} className="mr-1.5" /> Add Project
          </Button>
        </div>

        <div className="rounded-2xl border border-black/5 bg-white overflow-hidden">
          {loading ? (
            <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Order</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead className="hidden md:table-cell">Role</TableHead>
                  <TableHead className="hidden md:table-cell">Tags</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {projects.map((p) => (
                  <TableRow key={p.id} data-testid={`admin-row-${p.slug}`}>
                    <TableCell className="font-mono text-xs text-foreground/50">{p.order}</TableCell>
                    <TableCell className="font-medium">
                      {p.title}
                      {p.anonymized && <span className="ml-2 text-[10px] font-mono uppercase text-primary/60">anon</span>}
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-foreground/60">{p.role}</TableCell>
                    <TableCell className="hidden md:table-cell text-xs text-foreground/50">{(p.tags || []).join(", ")}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => window.open(`/projects/${p.slug}`, "_blank")} title="View">
                          <ExternalLink size={15} />
                        </Button>
                        <Button variant="ghost" size="icon" data-testid={`edit-${p.slug}`} onClick={() => openEdit(p)} title="Edit">
                          <Pencil size={15} />
                        </Button>
                        <Button variant="ghost" size="icon" data-testid={`delete-${p.slug}`} onClick={() => setDeleteId(p.id)} title="Delete" className="text-destructive hover:text-destructive">
                          <Trash2 size={15} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-lenis-prevent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Edit project" : "New project"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input id="title" data-testid="form-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="summary">Short description</Label>
              <Textarea id="summary" data-testid="form-summary" value={form.summary} onChange={(e) => set("summary", e.target.value)} className="mt-1.5" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="role">Role</Label>
                <Input id="role" value={form.role} onChange={(e) => set("role", e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="order">Order</Label>
                <Input id="order" type="number" value={form.order} onChange={(e) => set("order", e.target.value)} className="mt-1.5" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="tools">Tools (comma separated)</Label>
                <Input id="tools" value={form.tools} onChange={(e) => set("tools", e.target.value)} className="mt-1.5" placeholder="Figma, Miro" />
              </div>
              <div>
                <Label htmlFor="tags">Tags (comma separated)</Label>
                <Input id="tags" value={form.tags} onChange={(e) => set("tags", e.target.value)} className="mt-1.5" placeholder="Enterprise, Dashboards" />
              </div>
            </div>
            <div>
              <Label htmlFor="cover">Cover image URL</Label>
              <Input id="cover" value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} className="mt-1.5" />
            </div>
            <div className="flex items-center gap-8 py-1">
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="form-featured" />
                <span className="text-sm">Featured</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch checked={form.anonymized} onCheckedChange={(v) => set("anonymized", v)} data-testid="form-anonymized" />
                <span className="text-sm">Anonymized</span>
              </label>
            </div>
            {TEXT_FIELDS.map(([key, label]) => (
              <div key={key}>
                <Label htmlFor={key}>{label}</Label>
                <Textarea id={key} value={form[key]} onChange={(e) => set(key, e.target.value)} className="mt-1.5" rows={3} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-project-btn" onClick={save} disabled={saving} className="bg-primary hover:bg-[hsl(242,47%,28%)]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this project?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-btn" onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
